import type { AgentConfig } from '@form-factory/schema';
import { Role } from '@form-factory/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useAccess } from '@/hooks/useAccess';

export type OverridesFieldsProps = {};

export const OverridesFields = memo(
  function OverridesFields({}: OverridesFieldsProps) {
    const { t } = useLingui();
    const { hasAccess } = useAccess();

    const { schema } = useFieldsSchema(
      [
        {
          name: 'overrides',
          label: 'Enable overrides',
          type: 'checkbox',
          uiControl: 'switch',
          defaultValue: false,
          visible: hasAccess([Role.SuperAdmin]),
        },
        {
          $relevant: {
            $when: ({ formState }) =>
              !!(formState as TypedFormState<{ overrides: boolean }>).values
                ?.overrides,
            $fields: [
              {
                name: 'agentConfig.hybridAlpha',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'hybridAlpha',
                tooltip:
                  'An alpha of 1 is a pure vector search. An alpha of 0 is a pure keyword search.',
                type: 'number',
                defaultValue: 0.7,
              },
              {
                name: 'agentConfig.rewriteRetries',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'rewriteRetries',
                type: 'number',
                defaultValue: 1,
              },
              {
                name: 'agentConfig.historyLimit',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'historyLimit',
                type: 'number',
                defaultValue: 8,
              },
              {
                name: 'agentConfig.contextHistoryLimit',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'contextHistoryLimit',
                type: 'number',
                defaultValue: 8,
              },
              {
                name: 'agentConfig.retrievalCount',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'retrievalCount',
                type: 'number',
                defaultValue: 50,
              },
              {
                name: 'agentConfig.rerank',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'rerank',
                type: 'checkbox',
                uiControl: 'switch',
                defaultValue: true,
              },
              {
                $relevant: {
                  $when: ({ formState }) =>
                    !!(
                      formState as TypedFormState<{ agentConfig: AgentConfig }>
                    ).values?.agentConfig?.rerank,
                  $fields: [
                    {
                      name: 'agentConfig.rerankCount',
                      visible: hasAccess([Role.SuperAdmin]),
                      label: 'rerankCount',
                      type: 'number',
                      defaultValue: 20,
                    },
                    {
                      name: 'agentConfig.rerankCoefficient',
                      visible: hasAccess([Role.SuperAdmin]),
                      label: 'rerankCoefficient',
                      type: 'number',
                      defaultValue: 0.7,
                      tooltip:
                        'The coefficient that determines the influence of the reranked documents on the final result.',
                      uiProps: {
                        min: 0,
                        max: 1,
                        step: 0.1,
                      },
                    },
                    {
                      name: 'agentConfig.weightRerankCoefficient',
                      visible: hasAccess([Role.SuperAdmin]),
                      label: 'weightRerankCoefficient',
                      type: 'number',
                      defaultValue: 0.3,
                      tooltip:
                        'The coefficient that determines the influence of the document weights on the reranking.',
                      uiProps: {
                        min: 0,
                        max: 1,
                        step: 0.1,
                      },
                    },
                  ],
                },
              },
              {
                name: 'agentConfig.minScore',
                visible: hasAccess([Role.SuperAdmin]),
                label: 'minScore',
                type: 'number',
                defaultValue: 0,
              },
            ],
          },
        },
      ],
      [t, hasAccess],
    );

    return (
      <Card title='Overrides' variant='secondary'>
        <SchemaFields schema={schema} />
      </Card>
    );
  },
);
